// Settings.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../config/config-firebase";
import API_BASE from "../config/api";
import BottomTabBar from "../components/BottomTabBar";
import AppButton from "../components/AppButton";
import useMerchantStores from "../hooks/useMerchantStores";
import { isPromoLoginMode } from "../utils/adminMerchantMode";
import {
  ChevronRight,
  Settings as SettingsIcon,
  LayoutGrid,
  Pencil,
  User,
  Users,
  LogOut,
  Trash2,
  Info,
  Monitor,
  Lock,
  Check,
  Shield,
  Package,
  Upload,
  ScanLine,
  BarChart3,
  Map,
  Store,
  Building2,
  CreditCard,
  ShoppingCart,
} from "lucide-react";

export default function Settings() {
  const navigate = useNavigate();

  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");
  const [popupMessage, setPopupMessage] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");

  const [activeScreen, setActiveScreen] = useState("main");

  const { isTeamMember } = useMerchantStores(user?.uid);

  useEffect(() => {
    if (!localStorage.getItem("customer_id")) {
      alert("User not found 01");
      navigate("/");
    }
  }, []);

  const fetchMerchantDetails = async (
    uidOrCustomerId,
    useCustomerId = false,
  ) => {
    // /merchantDetailsByCustomer/:customer_id is currently commented out in
    // Backend/routes/merchantRoutes.js — any useCustomerId=true call 404s.
    // A promo device (PromoLogin.js) never has a Firebase uid to fall back
    // to, so it always takes this branch; rather than call an endpoint that
    // can never succeed and surface a scary "Could not load merchant
    // details" error, backfill what we already have locally (same pattern
    // MerchantHome.jsx uses) and leave the rest (email, logo — never
    // returned by /promoLogin) blank.
    if (useCustomerId && isPromoLoginMode()) {
      const cachedPhone = localStorage.getItem("phone");
      if (cachedPhone) setPhoneNumber(cachedPhone);
      const cachedName = localStorage.getItem("merchant_name");
      if (cachedName) setContactName(cachedName);
      return;
    }

    try {
      const url = useCustomerId
        ? `${API_BASE}/merchantDetailsByCustomer/${uidOrCustomerId}`
        : `${API_BASE}/merchantDetails/${uidOrCustomerId}`;
      const response = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) throw new Error("Failed to fetch merchant details");

      const data = await response.json();
      setContactName(data.data.name || "");
      setContactEmail(data.data.email || "");
      setPhoneNumber(data.data.mobile || "");
      setLogoPreview(data.data.logo || "");
    } catch (err) {
      console.error("Error fetching merchant details:", err);
      setError("Could not load merchant details.");
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return null;
    try {
      const formData = new FormData();
      formData.append("logo", file);
      formData.append("customerId", localStorage.getItem("customer_id"));

      const res = await fetch(`${API_BASE}/uploadLogo`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Logo upload failed");
      return data.logoUrl;
    } catch (err) {
      console.error("Logo upload error:", err);
      setError("Failed to upload logo.");
      return null;
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const customerId = localStorage.getItem("customer_id");
    const isAdminAsMerchant = !!localStorage.getItem("admin_token");
    if (!user && !(isAdminAsMerchant && customerId)) return;

    // Same root cause as fetchMerchantDetails above: a promo session's
    // `user.uid` is really just the customer_id (no Firebase uid exists),
    // and PUT /updateMerchantDetails/:user_id matches on the real Firebase
    // uid — this would always 404/"User not found" today. Say so plainly
    // instead of letting it fail as a generic "Failed to save settings".
    if (isPromoLoginMode()) {
      setError("Editing store details isn't available on this device yet.");
      return;
    }

    let uploadedLogoUrl = logoPreview;

    if (logoFile) {
      const url = await handleLogoUpload(logoFile);
      if (url) {
        uploadedLogoUrl = url;
        setLogoPreview(url);
      }
    }

    const updateData = {
      name: contactName,
      email: contactEmail,
      mobile: phoneNumber,
      logo: uploadedLogoUrl,
    };

    try {
      const url =
        isAdminAsMerchant && customerId
          ? `${API_BASE}/updateMerchantDetailsByCustomer/${customerId}`
          : `${API_BASE}/updateMerchantDetails/${user.uid}`;
      const response = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) throw new Error("Failed to update settings");

      setPopupMessage("Edited successfully!");

      if (isAdminAsMerchant && customerId) {
        fetchMerchantDetails(customerId, true);
      } else {
        fetchMerchantDetails(user.uid);
      }
    } catch (err) {
      console.error("Error updating settings:", err);
      setError("Failed to save settings.");
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;

    try {
      const response = await fetch(`${API_BASE}/deleteMerchant/${user.uid}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) throw new Error("Failed to delete account");

      setConfirmDelete(false);
      setPopupMessage("Your account has been deleted.");
      await auth.signOut();
      navigate("/");
    } catch (err) {
      console.error("Error deleting account:", err);
      setError("Failed to delete account.");
    }
  };

  useEffect(() => {
    const customerId = localStorage.getItem("customer_id");
    const isAdminAsMerchant = !!localStorage.getItem("admin_token");

    if (isAdminAsMerchant && customerId) {
      setUser({ uid: customerId });
      fetchMerchantDetails(customerId, true);
      return () => { };
    }

    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        fetchMerchantDetails(currentUser.uid);
      } else if (customerId) {
        setUser({ uid: customerId });
        fetchMerchantDetails(customerId, true);
      } else {
        alert("User not found 02");
        navigate("/login");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const screenShell =
    "min-h-[100dvh] bg-white w-full min-w-0 overflow-x-hidden flex flex-col pb-24 sm:pb-28";
  const contentWrap =
    "w-full max-w-lg sm:max-w-xl md:max-w-2xl mx-auto flex-1 flex flex-col min-w-0";
  const menuRow =
    "w-full flex items-center justify-between px-4 sm:px-5 py-3.5 sm:py-4 min-h-[48px] border-b border-gray-100 last:border-b-0 bg-white active:bg-gray-50 touch-manipulation transition-colors duration-200";
  const sectionHeader =
    "px-4 sm:px-5 pt-5 pb-2 text-xs font-bold uppercase tracking-wider text-gray-400";
  const sectionCard = "mx-4 sm:mx-5 mb-2 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden";

  const SettingsMenuItem = ({ icon: Icon, label, subtitle, onClick, iconClass = "text-gray-400" }) => (
    <AppButton variant="menu" type="button" onClick={onClick} className={menuRow}>
      <div className="flex items-center gap-3 min-w-0">
        <Icon size={18} className={`${iconClass} flex-shrink-0`} />
        <div className="min-w-0 text-left">
          <span className="text-sm sm:text-base font-medium text-gray-900 truncate block">
            {label}
          </span>
          {subtitle && (
            <span className="text-xs text-gray-400">{subtitle}</span>
          )}
        </div>
      </div>
      <ChevronRight size={18} className="text-gray-300 flex-shrink-0" />
    </AppButton>
  );

  const handleScannerClick = () => {
    sessionStorage.setItem("openScanner", "true");
    navigate("/user-scan");
  };

  const MainScreen = () => (
    <div className={screenShell}>
      <div className={contentWrap}>
        <div className="px-4 sm:px-5 pt-8 sm:pt-10 pb-2 flex items-start justify-between gap-3">
          <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full overflow-hidden bg-blue-100 flex items-center justify-center flex-shrink-0">
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="avatar"
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-blue-600 font-bold text-base sm:text-lg">
                {contactName?.charAt(0)?.toUpperCase() || "U"}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 sm:gap-3 mt-0.5 sm:mt-1 flex-shrink-0">
            <button
              type="button"
              className="text-gray-400 p-1 touch-manipulation"
              aria-label="Layout"
            >
              <LayoutGrid className="w-5 h-5 sm:w-[22px] sm:h-[22px]" />
            </button>
            <button
              type="button"
              className="text-gray-400 p-1 touch-manipulation"
              aria-label="Settings"
            >
              <SettingsIcon className="w-5 h-5 sm:w-[22px] sm:h-[22px]" />
            </button>
          </div>
        </div>

        <div className="px-4 sm:px-5 pt-1 pb-4 sm:pb-5">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 break-words leading-tight">
            Settings
          </h1>
          <p className="text-sm font-medium text-blue-600 mt-1">
            Welcome, {contactName?.split(" ")[0] || "there"}
          </p>
        </div>

        <div className="flex-1 min-w-0 pb-2">
          <p className={sectionHeader}>Store &amp; Inventory Operations</p>
          <div className={sectionCard}>
            <SettingsMenuItem
              icon={Package}
              label="View Items"
              onClick={() => navigate("/edit-data")}
              iconClass="text-emerald-500"
            />
            <SettingsMenuItem
              icon={Upload}
              label="Import Inventory Data"
              onClick={() => navigate("/import-data")}
              iconClass="text-blue-500"
            />
            <SettingsMenuItem
              icon={ScanLine}
              label="Scanner"
              onClick={handleScannerClick}
              iconClass="text-violet-500"
            />
            <SettingsMenuItem
              icon={ShoppingCart}
              label="Online Ordering"
              subtitle="Search items and build a cart"
              onClick={() => navigate("/online-ordering")}
              iconClass="text-sky-500"
            />
          </div>

          <p className={sectionHeader}>Analytics &amp; Insights</p>
          <div className={sectionCard}>
            <SettingsMenuItem
              icon={BarChart3}
              label="Sales Analytics"
              onClick={() => navigate("/sales-analytics")}
              iconClass="text-amber-500"
            />
            <SettingsMenuItem
              icon={Map}
              label="Map of the Store Sale"
              onClick={() => navigate("/map-sales")}
              iconClass="text-rose-500"
            />
          </div>

          <p className={sectionHeader}>Account &amp; Device Management</p>
          <div className={sectionCard}>
            <SettingsMenuItem
              icon={Monitor}
              label="Add Screen"
              onClick={() => navigate("/modes")}
              iconClass="text-blue-500"
            />
            {!isTeamMember && (
              <SettingsMenuItem
                icon={Building2}
                label="Organisation"
                subtitle="Business details and logo"
                onClick={() => navigate("/organisation")}
              />
            )}
            {!isTeamMember && (
              <SettingsMenuItem
                icon={Store}
                label="My stores"
                subtitle="Add and manage store locations"
                onClick={() => navigate("/stores")}
              />
            )}
            <SettingsMenuItem
              icon={Lock}
              label="Change Password"
              onClick={() => alert("Change Password coming soon")}
              iconClass="text-gray-500"
            />
          </div>

          <p className={sectionHeader}>Your Account</p>
          <div className={sectionCard}>
            <SettingsMenuItem
              icon={User}
              label="Profile"
              onClick={() => setActiveScreen("profile")}
            />
            {!isTeamMember && (
              <SettingsMenuItem
                icon={CreditCard}
                label="Billing"
                subtitle="Update payment method"
                onClick={() => navigate("/billing")}
                iconClass="text-blue-500"
              />
            )}
            {!isTeamMember && (
              <SettingsMenuItem
                icon={Users}
                label="Team Members"
                subtitle="Manage store access"
                onClick={() => navigate("/team-members")}
              />
            )}
          </div>

          <p className={sectionHeader}>More</p>
          <div className={sectionCard}>
            <SettingsMenuItem
              icon={Info}
              label="Terms and Conditions"
              onClick={() => navigate("/privacy-policy")}
            />
            <SettingsMenuItem
              icon={Info}
              label="About Ezwinds"
              onClick={() => setActiveScreen("about")}
            />
          </div>

          {!localStorage.getItem("admin_token") && (
            <div className="mx-4 sm:mx-5 mt-3 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <AppButton
                variant="destructive-row"
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="border-b-0"
              >
                <Trash2 size={18} className="text-red-400 flex-shrink-0" />
                <span className="text-sm sm:text-base font-medium text-red-500">
                  Delete My Account
                </span>
              </AppButton>
            </div>
          )}
        </div>

        <div className="px-4 sm:px-5 pt-4 pb-2 sm:pb-4 mt-auto">
          <p className="text-sm sm:text-base font-extrabold text-gray-900">
            Ezwinds
          </p>
          <p className="text-xs text-gray-400 mb-4 sm:mb-5">
            Version 1.0 February, 2026
          </p>

          <button
            type="button"
            onClick={async () => {
              await auth.signOut();
              localStorage.removeItem("customer_id");
              localStorage.removeItem("admin_token");
              navigate("/");
            }}
            className="w-full bg-red-50 text-red-500 border border-red-100 font-semibold text-sm py-3.5 sm:py-4 rounded-full flex items-center justify-center gap-2 touch-manipulation"
          >
            <LogOut size={16} /> Log Out
          </button>
        </div>
      </div>

      <BottomTabBar />
    </div>
  );

  const ProfileScreen = () => (
    <div className={screenShell}>
      <div className={`${contentWrap} pb-4`}>
        <div className="px-4 sm:px-5 pt-8 sm:pt-10 pb-4">
          <button
            type="button"
            onClick={() => setActiveScreen("main")}
            className="text-sm text-gray-500 flex items-center gap-1 mb-4 py-1 touch-manipulation"
          >
            ← Back
          </button>
          <h1 className="text-xl sm:text-2xl font-extrabold text-gray-900">
            Settings
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Your Profile</p>
        </div>

        <div className="flex justify-center mb-6 sm:mb-8 px-4">
          <div className="relative">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden bg-blue-100 flex items-center justify-center">
              {logoPreview ? (
                <img
                  src={logoPreview}
                  alt="avatar"
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-blue-600 font-bold text-xl sm:text-2xl">
                  {contactName?.charAt(0)?.toUpperCase() || "U"}
                </span>
              )}
            </div>
            <label
              htmlFor="logo-upload"
              className="absolute bottom-0 right-0 w-7 h-7 sm:w-6 sm:h-6 bg-blue-600 rounded-full flex items-center justify-center cursor-pointer shadow-md touch-manipulation"
            >
              <Pencil size={12} className="text-white" />
            </label>
            <input
              id="logo-upload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                if (!e.target.files[0]) return;
                setLogoFile(e.target.files[0]);
                setLogoPreview(URL.createObjectURL(e.target.files[0]));
              }}
            />
          </div>
        </div>

        <form
          onSubmit={handleSave}
          className="px-4 sm:px-5 space-y-3 flex-1 w-full min-w-0"
        >
          {error && (
            <p className="text-xs sm:text-sm text-red-500 mb-2">{error}</p>
          )}

          <input
            type="text"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            placeholder="Name"
            required
            className="w-full bg-gray-100 rounded-2xl px-4 py-3.5 sm:py-4 text-base sm:text-sm text-gray-900 border-none outline-none"
          />

          <input
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="Email"
            required
            className="w-full bg-gray-100 rounded-2xl px-4 py-3.5 sm:py-4 text-base sm:text-sm text-gray-900 border-none outline-none"
          />

          <input
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="Phone number"
            required
            className="w-full bg-gray-100 rounded-2xl px-4 py-3.5 sm:py-4 text-base sm:text-sm text-gray-900 border-none outline-none"
          />

          <div className="pt-2 sm:pt-3">
            <button
              type="submit"
              className="w-full bg-blue-600 text-white font-bold text-sm py-3.5 sm:py-4 rounded-full flex items-center justify-center gap-2 touch-manipulation"
            >
              <Check size={15} /> Save Changes
            </button>
          </div>
        </form>
      </div>

      <BottomTabBar />
    </div>
  );

  const AboutScreen = () => (
    <div className={screenShell}>
      <div className={contentWrap}>
        <div className="px-4 sm:px-5 pt-8 sm:pt-10 pb-4">
          <button
            type="button"
            onClick={() => setActiveScreen("main")}
            className="text-sm text-gray-500 flex items-center gap-1 mb-4 py-1 touch-manipulation"
          >
            ← Back
          </button>
        </div>

        <div className="flex justify-center px-6 sm:px-10 pt-2 sm:pt-4 pb-6 sm:pb-8">
          {logoPreview ? (
            <img
              src={logoPreview}
              alt="Ezwinds logo"
              className="w-28 h-28 sm:w-36 sm:h-36 rounded-full object-cover"
            />
          ) : (
            <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-pink-500 flex items-center justify-center shadow-lg px-3">
              <span className="text-white font-extrabold text-lg sm:text-2xl text-center">
                Ezwinds
              </span>
            </div>
          )}
        </div>

        <div className="px-4 sm:px-5 pb-4">
          <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 mb-3">
            About Ezwinds
          </h2>
          <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-5 sm:mb-6">
            Ezwinds is a digital display management platform that helps
            businesses create and manage promo ads, menus, and TV screens from
            one simple app. Designed to make digital signage effortless for
            merchants of every size.
          </p>
          <p className="text-sm sm:text-base text-gray-600 mb-2">
            If you need help or have any questions, feel free to contact us by
            email.
          </p>
          <p className="text-sm sm:text-base font-bold text-gray-900 break-all">
            hello@mydomain.com
          </p>
        </div>
      </div>

      <BottomTabBar />
    </div>
  );


  return (
    <>
      {activeScreen === "profile" ? (
        <ProfileScreen />
      ) : activeScreen === "about" ? (
        <AboutScreen />
      ) : (
        <MainScreen />
      )}

      {popupMessage && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4 sm:p-8">
          <div className="bg-white rounded-2xl p-5 sm:p-6 w-full max-w-xs sm:max-w-sm shadow-2xl text-center">
            <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-3">
              <Check size={22} className="text-green-500" />
            </div>
            <p className="text-sm sm:text-base font-semibold text-gray-900 mb-4">
              {popupMessage}
            </p>
            <button
              type="button"
              onClick={() => setPopupMessage("")}
              className="w-full bg-blue-600 text-white font-bold py-3 sm:py-3.5 rounded-full text-sm touch-manipulation"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4 sm:p-8">
          <div className="bg-white rounded-2xl p-5 sm:p-6 w-full max-w-xs sm:max-w-sm shadow-2xl">
            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mb-4">
              <Trash2 size={22} className="text-red-500" />
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
              Delete account
            </h3>
            <p className="text-sm sm:text-base text-gray-500 mb-5 sm:mb-6">
              Are you sure you want to delete your account? This cannot be
              undone.
            </p>
            <div className="flex flex-col-reverse sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="flex-1 border border-gray-300 text-gray-700 font-semibold py-3 rounded-full text-sm touch-manipulation"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteAccount}
                className="flex-1 bg-red-500 text-white font-bold py-3 rounded-full text-sm touch-manipulation"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
}
